import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import { useParams, useSearchParams } from "react-router-dom";
import { FETCH_STOCK_CHECK_ALC_STOCKS } from "../graphql";

const toComparableNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCompareRowState = (item, hasShopifyInstance) => {
  const wisQty = toComparableNumber(item?.wis_qty);
  const alcTechQty = toComparableNumber(item?.alc_tech_qty);
  const alcAvailableQty = toComparableNumber(item?.alc_available_qty);
  const shopifyOnHand = toComparableNumber(item?.shopify_on_hand);
  const shopifyAvailable = toComparableNumber(item?.shopify_available);

  const shopifyMinusAlcAvailable =
    shopifyAvailable !== null && alcAvailableQty !== null
      ? shopifyAvailable - alcAvailableQty
      : null;

  const hasAnyAlcValue = alcTechQty !== null || alcAvailableQty !== null;
  const hasAnyShopifyValue =
    shopifyOnHand !== null || shopifyAvailable !== null;

  const isShopifyOnHandMatchingAlc =
    shopifyOnHand !== null &&
    ((alcTechQty !== null && shopifyOnHand === alcTechQty) ||
      (alcAvailableQty !== null && shopifyOnHand === alcAvailableQty));

  const isShopifyAvailableMatchingAlc =
    shopifyAvailable !== null &&
    ((alcTechQty !== null && shopifyAvailable === alcTechQty) ||
      (alcAvailableQty !== null && shopifyAvailable === alcAvailableQty));

  const hasShopifyMatchingAlc =
    isShopifyOnHandMatchingAlc || isShopifyAvailableMatchingAlc;

  const isAlcTechLessThanAlcAvail =
    alcTechQty !== null &&
    alcAvailableQty !== null &&
    alcTechQty < alcAvailableQty;

  const hasMismatchWithCheckData = hasShopifyInstance
    ? isAlcTechLessThanAlcAvail ||
      (wisQty !== null &&
        hasAnyShopifyValue &&
        wisQty !== shopifyOnHand &&
        wisQty !== shopifyAvailable) ||
      (hasAnyAlcValue && hasAnyShopifyValue && !hasShopifyMatchingAlc)
    : wisQty !== null && alcAvailableQty !== null && wisQty !== alcAvailableQty;

  return {
    shopifyMinusAlcAvailable,
    hasMismatchWithCheckData,
  };
};

const WarehouseComparePage = () => {
  const { stockCategoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({ q: "" });
  const q = searchParams.get("q") || "";
  const normalizedQ = q.trim().toLowerCase();

  const { loading, error, data } = useQuery(FETCH_STOCK_CHECK_ALC_STOCKS, {
    variables: { stockCategoryId },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const category = data?.stockCategory;
  const res = JSON.parse(data.stockCheckAlcStocks);
  const hasShopifyInstance = Array.isArray(res?.shopify_instance)
    ? res.shopify_instance.length > 0
    : Boolean(res?.shopify_instance);

  const sortedRows = [...res.results].sort((a, b) =>
    String(a?.sku || "").localeCompare(String(b?.sku || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
  const filteredRows = sortedRows.filter((item) => {
    if (!normalizedQ) return true;
    const number = String(item?.sku || "").toLowerCase();
    const name = String(item?.product_name || "").toLowerCase();
    const alcSku = String(item?.alc_sku || "").toLowerCase();
    const shopifySku = String(item?.shopify_sku || "").toLowerCase();
    return (
      number.includes(normalizedQ) ||
      name.includes(normalizedQ) ||
      alcSku.includes(normalizedQ) ||
      shopifySku.includes(normalizedQ)
    );
  });

  return (
    <PageV2
      title={`${category?.name || "Warehouse"} - Inventory Compare`}
      subtitle={
        hasShopifyInstance
          ? "Compare the WIS stock with ALC and Shopify"
          : "Compare the WIS stock with ALC"
      }
      backTo="./.."
      className="h-screen"
    >
      <div className="h-full flex flex-col">
        <div className="mb-4">
          <SearchBar
            initialQuery={q}
            placeholder="Search by product number or name"
            onChange={(nextQ) => setSearchParams({ q: nextQ })}
          />
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col">
          <div className="flex-1 overflow-auto relative">
            {filteredRows.length === 0 ? (
              <div className="p-8 text-sm text-gray-500">
                No products found in this warehouse.
              </div>
            ) : (
              <div className="whitespace-nowrap">
                <table>
                  <thead>
                    <tr className="sticky top-0 z-10 backdrop-blur bg-gray-50 bg-opacity-80 dark:bg-gray-700/50">
                      <th className="px-4 py-3">
                        Product({filteredRows.length})
                      </th>
                      {hasShopifyInstance && (
                        <>
                          <th className="px-4 text-right">Shopify On Hand</th>
                          <th className="px-4 text-right">Shopify Avail</th>
                          <th className="px-4 text-right">Shopify Committed</th>
                        </>
                      )}
                      <th className="px-4 text-right">WIS Qty</th>
                      <th className="px-4 text-right">ALC Tech</th>
                      <th className="px-4 text-right">ALC Avail</th>
                      {hasShopifyInstance && (
                        <th className="px-4 text-right">S-A Avail</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((item) => {
                      const {
                        shopifyMinusAlcAvailable,
                        hasMismatchWithCheckData,
                      } = getCompareRowState(item, hasShopifyInstance);

                      return (
                        <tr
                          key={item.product_id}
                          className={`border-y border-gray-100 dark:border-gray-700 ${
                            hasMismatchWithCheckData
                              ? "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300"
                              : ""
                          } hover:bg-sky-50 dark:hover:bg-sky-900/20`}
                        >
                          <td className="px-4">
                            [{item.sku}] {item.product_name} (
                            {item.items_per_set}/ctn)
                          </td>
                          {hasShopifyInstance && (
                            <>
                              <td className="px-4 text-right">
                                {item.shopify_on_hand ?? "-"}
                              </td>
                              <td className="px-4 text-right">
                                {item.shopify_available ?? "-"}
                              </td>
                              <td className="px-4 text-right">
                                {item.shopify_committed ?? "-"}
                              </td>
                            </>
                          )}
                          <td className="px-4 text-right">{item.wis_qty}</td>
                          <td className="px-4 text-right">
                            {item.alc_tech_qty ?? "-"}
                          </td>
                          <td className="px-4 text-right">
                            {item.alc_available_qty ?? "-"}
                          </td>
                          {hasShopifyInstance && (
                            <td className="px-4 text-right">
                              {shopifyMinusAlcAvailable ?? "-"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageV2>
  );
};

export default WarehouseComparePage;
